/**
 * SVG element description for annotation primitives.
 *
 * Two consumers need the same SVG: the live editor overlay (React elements) and
 * the parity test (a standalone `<svg>` markup string it rasterizes and diffs
 * against the Pillow bake). If those two built their attributes independently
 * they could drift, and the parity test would then be validating a copy of the
 * overlay rather than the overlay itself.
 *
 * So this module emits a neutral `SvgElementSpec` — tag plus attribute bag —
 * and both consumers serialize the same bag. `annotationGeometry.ts` owns the
 * numbers; this file owns only how they become SVG attributes.
 */

import {
  ANNOTATION_FONT_FAMILY,
  ANNOTATION_FONT_WEIGHT,
  buildAnnotationPrimitives,
  type AnnotationPrimitive,
} from './annotationGeometry'
import type { ImageAnnotationDict } from '../../../../../generated/nodeDataTypes'

export interface SvgElementSpec {
  tag: 'polyline' | 'rect' | 'ellipse' | 'polygon' | 'circle' | 'text'
  attrs: Record<string, string | number>
  /** Present only for `<text>`. */
  textContent?: string
}

function formatPoints(points: { x: number; y: number }[]): string {
  return points.map((point) => `${round3(point.x)},${round3(point.y)}`).join(' ')
}

/**
 * Round to 3 decimals. Keeps the emitted markup stable across platforms (so the
 * committed expectation file in the parity test is not hostage to float noise)
 * while staying far below one device pixel.
 */
function round3(value: number): number {
  return Math.round(value * 1000) / 1000
}

/** Describe one primitive as an SVG tag + attributes. */
export function primitiveToSvgElement(primitive: AnnotationPrimitive): SvgElementSpec {
  switch (primitive.shape) {
    case 'polyline':
      return {
        tag: 'polyline',
        attrs: {
          points: formatPoints(primitive.points),
          fill: 'none',
          stroke: primitive.color,
          'stroke-width': round3(primitive.strokeWidth),
          // Rule 2 — the renderer stamps round caps that Pillow's `joint="curve"`
          // does not produce on its own.
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
        },
      }
    case 'rect':
      return {
        tag: 'rect',
        attrs: primitive.filled
          ? {
              x: round3(primitive.x),
              y: round3(primitive.y),
              width: round3(primitive.width),
              height: round3(primitive.height),
              fill: primitive.color,
              stroke: 'none',
            }
          : {
              x: round3(primitive.x),
              y: round3(primitive.y),
              width: round3(primitive.width),
              height: round3(primitive.height),
              fill: 'none',
              stroke: primitive.color,
              'stroke-width': round3(primitive.strokeWidth),
              'stroke-linejoin': 'round',
            },
      }
    case 'ellipse':
      return {
        tag: 'ellipse',
        attrs: primitive.filled
          ? {
              cx: round3(primitive.cx),
              cy: round3(primitive.cy),
              rx: round3(primitive.rx),
              ry: round3(primitive.ry),
              fill: primitive.color,
              stroke: 'none',
            }
          : {
              cx: round3(primitive.cx),
              cy: round3(primitive.cy),
              rx: round3(primitive.rx),
              ry: round3(primitive.ry),
              fill: 'none',
              stroke: primitive.color,
              'stroke-width': round3(primitive.strokeWidth),
            },
      }
    case 'polygon':
      return {
        tag: 'polygon',
        attrs: {
          points: formatPoints(primitive.points),
          fill: primitive.color,
          stroke: 'none',
        },
      }
    case 'dot':
      return {
        tag: 'circle',
        attrs: {
          cx: round3(primitive.cx),
          cy: round3(primitive.cy),
          r: round3(primitive.r),
          fill: primitive.color,
          stroke: 'none',
        },
      }
    case 'text':
      return {
        tag: 'text',
        attrs: {
          x: round3(primitive.x),
          y: round3(primitive.y),
          fill: primitive.color,
          'font-size': round3(primitive.fontSize),
          'font-family': ANNOTATION_FONT_FAMILY,
          'font-weight': ANNOTATION_FONT_WEIGHT,
          // Rule 3 — Pillow's `anchor="la"` is left / ascender.
          'text-anchor': 'start',
          'dominant-baseline': 'hanging',
          stroke: 'none',
        },
        textContent: primitive.text,
      }
  }
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function serializeElement(spec: SvgElementSpec): string {
  const attrs = Object.entries(spec.attrs)
    .map(([name, value]) => `${name}="${escapeXml(String(value))}"`)
    .join(' ')
  if (spec.textContent === undefined) return `<${spec.tag} ${attrs} />`
  return `<${spec.tag} ${attrs}>${escapeXml(spec.textContent)}</${spec.tag}>`
}

/**
 * Render a spec to a standalone `<svg>` document string.
 *
 * Used by the parity test, which rasterizes this through a data URL and diffs it
 * against the Pillow bake of the same spec. Deliberately the *same*
 * `buildAnnotationPrimitives` the editor renders from — that shared call is what
 * makes the test meaningful.
 */
export function renderAnnotationSvgMarkup(
  annotations: ImageAnnotationDict[],
  width: number,
  height: number,
): string {
  const body = buildAnnotationPrimitives(annotations, width, height)
    .map((primitive) => serializeElement(primitiveToSvgElement(primitive)))
    .join('')
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" ` +
    `viewBox="0 0 ${width} ${height}">${body}</svg>`
  )
}
